import { Badge } from "@/components/atoms/Badge";
import { Tooltip } from "@/components/atoms/Tooltip";
import type { CampaignLabel } from "@/types/campaign";

function labelName(label: CampaignLabel): string {
  const name = typeof label.name === "string" ? label.name.trim() : "";
  return name || String(label.id ?? "");
}

/** Single-line label chip row for the campaigns table — one primary tag, +N for the rest. */
export function CampaignLabelCell({ labels }: { labels: CampaignLabel[] | null | undefined }) {
  const items = labels ?? [];
  if (items.length === 0) {
    return <span className="text-bridge-subtle">—</span>;
  }

  const [primary, ...rest] = items;
  const primaryName = labelName(primary);
  const extraCount = rest.length;
  const remainingNames = rest.map(labelName);

  return (
    <div className="flex h-12 min-h-12 max-h-12 w-full min-w-0 flex-nowrap items-center justify-center gap-1 overflow-hidden">
      <Badge
        key={String(primary.id ?? primaryName)}
        tone="neutral"
        className={
          extraCount > 0
            ? "h-5 min-w-0 max-w-[calc(100%-2.75rem)] shrink overflow-hidden whitespace-nowrap"
            : "h-5 min-w-0 max-w-full shrink overflow-hidden whitespace-nowrap"
        }
      >
        <span className="block truncate">{primaryName}</span>
      </Badge>
      {extraCount > 0 && (
        <Tooltip
          scrollMaxHeight="min(16rem, 50vh)"
          content={
            <div>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-300">
                {extraCount} more label{extraCount === 1 ? "" : "s"}
              </div>
              <ul className="space-y-1">
                {remainingNames.map((name, index) => (
                  <li key={`${name}-${index}`} className="truncate">
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          }
          side="bottom-right"
          variant="muted"
          hoverOnly
        >
          <span
            className="inline-flex shrink-0"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Badge
              key={`${String(primary.id ?? primaryName)}-more`}
              tone="neutral"
              className="h-5 cursor-default whitespace-nowrap tabular-nums"
              aria-label={`${extraCount} more label${extraCount === 1 ? "" : "s"}`}
            >
              +{extraCount}
            </Badge>
          </span>
        </Tooltip>
      )}
    </div>
  );
}
